import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Pin } from "lucide-react";
import { api } from "../services/api";
import { useUi } from "../stores/ui";
import { can, effectiveRequirementStatus, type Paginated, type RosterMember, type SessionUser, type Team } from "../types";
import { Button, Card, EmptyState, Input, PageHeader, Select, Skeleton, StatusPill, TeamBadge } from "../components/ui";
import { AnimatedNumber, ProgressBar } from "../components/feedback";
import { cn } from "../utils/cn";

export function RosterPage({ archived = false }: { archived?: boolean }) {
  const user = useUi((s) => s.user);
  const [search, setSearch] = useState("");
  const [teamId, setTeamId] = useState("");
  const [status, setStatus] = useState("ALL");
  const [sort, setSort] = useState("name");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const teams = useQuery({ queryKey: ["teams"], queryFn: () => api<{ teams: Team[] }>("/api/admin/teams") });
  const roster = useQuery({
    queryKey: ["roster", { search, teamId, status, sort, page, limit, archived }],
    queryFn: () => {
      const params = new URLSearchParams({
        search,
        status,
        sort,
        page: String(page),
        limit: String(limit),
        archived: archived ? "true" : "false",
      });
      if (teamId) params.set("teamId", teamId);
      return api<Paginated<RosterMember>>(`/api/roster?${params}`);
    },
  });

  const selfProfile = useQuery({
    queryKey: ["profile", user?.discordId],
    queryFn: () => api<{ member: RosterMember }>(`/api/roster/${user!.discordId}`),
    enabled: Boolean(user?.discordId && user.isHrMember),
  });
  const items = roster.data?.items ?? [];
  const showId = can(user, "VIEW_MEMBER_IDS");
  const visible = useMemo(
    () => pinCurrentUser(items, user, selfProfile.data?.member, { search, teamId, status, archived }),
    [items, user, selfProfile.data?.member, search, teamId, status, archived],
  );

  return (
    <div>
      <PageHeader
        title={archived ? "HR Archive" : "Human Resources Roster"}
        subtitle={archived ? "Former HR members with preserved history." : "Every current holder of the HR role."}
      />
      <Card className="mb-4">
        <div className="hidden gap-3 md:grid md:grid-cols-2 xl:grid-cols-5">
          <Input placeholder="Search by name" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          <Select value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            <option value="">All teams</option>
            {teams.data?.teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
          </Select>
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </Select>
          <Select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="name">Alphabetical</option>
            <option value="interviews">Interviews</option>
            <option value="trainings">Trainings</option>
            <option value="activity">Monthly activity</option>
          </Select>
          <Select value={String(limit)} onChange={(e) => setLimit(Number(e.target.value))}>
            <option value="10">10 / page</option>
            <option value="25">25 / page</option>
            <option value="50">50 / page</option>
          </Select>
        </div>
        <div className="space-y-3 md:hidden">
          <Input placeholder="Search by name" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          <details className="rounded-md border border-white/10 bg-ink-950">
            <summary className="cursor-pointer list-none px-3 py-2.5 text-sm font-medium text-zinc-200">Filters and sort</summary>
            <div className="grid gap-3 border-t border-white/10 p-3">
              <Select value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                <option value="">All teams</option>
                {teams.data?.teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
              </Select>
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="ALL">All statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </Select>
              <Select value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="name">Alphabetical</option>
                <option value="interviews">Interviews</option>
                <option value="trainings">Trainings</option>
                <option value="activity">Monthly activity</option>
              </Select>
              <Select value={String(limit)} onChange={(e) => setLimit(Number(e.target.value))}>
                <option value="10">10 / page</option>
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
              </Select>
            </div>
          </details>
        </div>
      </Card>
      {roster.isLoading ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : visible.length === 0 ? (
        <EmptyState
          title={search ? "No HR members matched your search." : archived ? "No archived HR members." : status === "INACTIVE" ? "Everyone currently meets their activity requirement 🎉" : "No HR members on the roster yet."}
        />
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-lg border border-white/10 lg:block">
            <table className="w-full text-left text-sm">
              <thead className="bg-white/5 text-zinc-400">
                <tr>
                  <th className="px-4 py-3">Member</th>
                  <th>Discord Username</th>
                  <th>Team</th>
                  <th>Interviews Hosted</th>
                  <th>Trainings Hosted</th>
                  <th>Monthly Activity</th>
                  <th>Status</th>
                  {user?.isManager ? <th>Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {visible.map((member) => {
                  const pinned = isCurrentUser(member, user);
                  return (
                    <tr
                      key={member.discordId ?? member.username}
                      className={cn(
                        "border-t border-white/5",
                        pinned
                          ? "[&>td]:bg-accent/20 [&>td]:shadow-[inset_0_0_0_9999px_rgb(var(--accent)/0.12)] hover:[&>td]:bg-accent/25"
                          : "hover:bg-white/[0.02]",
                      )}
                    >
                      <td className="px-4 py-3">
                        <Link to={`/roster/${member.discordId}`} className="flex items-center gap-3">
                          <MemberAvatar member={member} pinned={pinned} className="h-10 w-10" />
                          <div>
                            <p className="flex items-center gap-1.5 font-semibold">
                              {pinned ? (
                                <span title="Pinned" className="inline-flex">
                                  <Pin className="h-3.5 w-3.5 shrink-0 fill-accent text-accent" aria-hidden />
                                </span>
                              ) : null}
                              {member.displayName}
                            </p>
                            {showId ? <p className="text-xs text-zinc-500">{member.discordId}</p> : null}
                          </div>
                        </Link>
                      </td>
                      <td>@{member.username}</td>
                      <td>
                        <TeamCell member={member} />
                      </td>
                      <td className="font-semibold">
                        <StatCell member={member} type="INTERVIEW" value={member.allTimeInterviews} />
                      </td>
                      <td className="font-semibold">
                        <StatCell member={member} type="TRAINING" value={member.allTimeTrainings} />
                      </td>
                      <td>
                        <MonthlyActivity member={member} barClassName="mt-1 w-32" />
                      </td>
                      <td><StatusPill status={effectiveRequirementStatus(member)} /></td>
                      {user?.isManager ? (
                        <td>
                          <Link to={`/roster/${member.discordId}`} className="text-accent">View</Link>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="grid gap-3 lg:hidden">
            {visible.map((member) => {
              const pinned = isCurrentUser(member, user);
              return (
              <Link
                key={member.discordId ?? member.username}
                to={`/roster/${member.discordId}`}
                className={cn(
                  "relative overflow-hidden rounded-lg border bg-ink-900 p-4",
                  pinned
                    ? "border-accent/50 bg-accent/15 shadow-[inset_0_0_0_9999px_rgb(var(--accent)/0.10)]"
                    : "border-white/10",
                )}
              >
                <div className="flex items-start gap-3">
                  <MemberAvatar member={member} pinned={pinned} className="h-11 w-11" />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 truncate font-semibold">
                      {pinned ? <Pin className="h-3.5 w-3.5 shrink-0 fill-accent text-accent" aria-hidden /> : null}
                      <span className="truncate">{member.displayName}</span>
                    </p>
                    <p className="truncate text-sm text-zinc-500">@{member.username}</p>
                    <div className="mt-2"><TeamCell member={member} /></div>
                  </div>
                  <StatusPill status={effectiveRequirementStatus(member)} />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-zinc-500">Interviews</p>
                    <p className="font-medium"><StatCell member={member} type="INTERVIEW" value={member.allTimeInterviews} /></p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Trainings</p>
                    <p className="font-medium"><StatCell member={member} type="TRAINING" value={member.allTimeTrainings} /></p>
                  </div>
                </div>
                <div className="mt-2 text-sm text-zinc-400">
                  <MonthlyActivity member={member} barClassName="mt-2" />
                </div>
              </Link>
              );
            })}
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-zinc-400">
            <span>{roster.data?.total ?? 0} members</span>
            <div className="flex gap-2">
              <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
              <Button variant="ghost" disabled={page * limit >= (roster.data?.total ?? 0)} onClick={() => setPage(page + 1)}>Next</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function isCurrentUser(member: RosterMember, user: SessionUser | null | undefined) {
  if (!user) return false;
  if (member.discordId && user.discordId === member.discordId) return true;
  return Boolean(member.username && member.username === user.username);
}

function matchesRosterFilters(
  member: RosterMember,
  filters: { search: string; teamId: string; status: string; archived: boolean },
) {
  if (filters.archived ? member.isActiveHr : !member.isActiveHr) return false;
  if (filters.status === "ACTIVE" || filters.status === "INACTIVE") {
    if (effectiveRequirementStatus(member) !== filters.status) return false;
  }
  if (filters.teamId) {
    const ids = new Set([member.primaryTeam?.id, ...member.teams.map((team) => team.id)].filter(Boolean));
    if (!ids.has(filters.teamId)) return false;
  }
  const query = filters.search.trim().toLowerCase();
  if (query) {
    const hay = [member.displayName, member.username, member.nickname, member.globalName, member.discordId]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!hay.includes(query)) return false;
  }
  return true;
}

function pinCurrentUser(
  items: RosterMember[],
  user: SessionUser | null | undefined,
  fetchedSelf: RosterMember | undefined,
  filters: { search: string; teamId: string; status: string; archived: boolean },
) {
  const others = items.filter((member) => !isCurrentUser(member, user));
  const candidate = items.find((member) => isCurrentUser(member, user)) ?? fetchedSelf;
  if (!candidate || !isCurrentUser(candidate, user) || !matchesRosterFilters(candidate, filters)) {
    return others;
  }
  return [candidate, ...others];
}

function MemberAvatar({
  member,
  pinned,
  className,
}: {
  member: RosterMember;
  pinned: boolean;
  className?: string;
}) {
  return (
    <span className="relative shrink-0">
      <img src={member.avatarUrl} alt="" className={cn("rounded-full", className)} />
      {pinned ? (
        <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-white shadow-sm">
          <Pin className="h-2.5 w-2.5 fill-current" aria-hidden />
        </span>
      ) : null}
    </span>
  );
}

function monthlyActivityCount(member: RosterMember) {
  let total = 0;
  if (canHost(member, "INTERVIEW")) total += member.monthInterviews;
  if (canHost(member, "TRAINING")) total += member.monthTrainings;
  return total;
}

function monthlyLabel(member: RosterMember) {
  const parts: string[] = [];
  if (canHost(member, "INTERVIEW")) {
    parts.push(`${member.monthInterviews} ${member.monthInterviews === 1 ? "Interview" : "Interviews"}`);
  }
  if (canHost(member, "TRAINING")) {
    parts.push(`${member.monthTrainings} ${member.monthTrainings === 1 ? "Training" : "Trainings"}`);
  }
  if (!parts.length) return "Not in team";
  return parts.join(" • ");
}

function MonthlyActivity({ member, barClassName }: { member: RosterMember; barClassName?: string }) {
  return (
    <>
      {monthlyLabel(member)}
      {monthlyActivityCount(member) > 0 ? (
        <div className={barClassName}>
          <ProgressBar value={member.requirementProgress} />
        </div>
      ) : null}
    </>
  );
}

function assignedTeams(member: RosterMember) {
  return member.teams.length ? member.teams : member.primaryTeam ? [member.primaryTeam] : [];
}

function canHost(member: RosterMember, type: "INTERVIEW" | "TRAINING") {
  const teams = assignedTeams(member);
  if (!teams.length) return false;
  return teams.some((team) => {
    if (team.systemKey === type) return true;
    const allowed = team.allowedActivityTypes;
    if (!allowed?.length) return false;
    return allowed.includes(type);
  });
}

function TeamCell({ member }: { member: RosterMember }) {
  const teams = assignedTeams(member);
  if (!teams.length) return <span className="text-zinc-500">Not in team</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {teams.map((team) => (
        <TeamBadge key={team.id} team={team} />
      ))}
    </div>
  );
}

function StatCell({
  member,
  type,
  value,
}: {
  member: RosterMember;
  type: "INTERVIEW" | "TRAINING";
  value: number;
}) {
  if (!canHost(member, type)) return <span className="text-zinc-500">Not in team</span>;
  return <AnimatedNumber value={value} />;
}
